import React, { useEffect, useState, useCallback } from 'react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import {
  Settings2, X, GripVertical, Eye, EyeOff, Save,
  BarChart3, Package, Activity, Newspaper, Megaphone, Hash, TrendingUp, ListTodo
} from 'lucide-react';
import Sidebar from '../components/Sidebar';
import api from '../services/api';
import usePageTitle from '../hooks/usePageTitle';

// Dashboard layouts by account type
import SellerDashboard from '../components/dashboard/SellerDashboard';
import BuyerDashboard from '../components/dashboard/BuyerDashboard';
import BrokerDashboard from '../components/dashboard/BrokerDashboard';

// Module metadata for the settings panel
const MODULE_META = {
  animated_counters: { label: 'Animated Counters', icon: BarChart3, description: 'Stats with count-up animation' },
  latest_opportunities: { label: 'Opportunities', icon: Package, description: 'Deal listings' },
  activity_feed: { label: 'Activity Feed', icon: Activity, description: 'Live platform activity' },
  news_articles: { label: 'News Articles', icon: Newspaper, description: 'Latest news carousel' },
  announcements: { label: 'Announcements', icon: Megaphone, description: 'Platform announcements' },
  commodity_tracker: { label: 'Live Commodity Tracker', icon: Hash, description: 'Commodity price grid' },
  opportunity_ticker: { label: 'Opportunity Ticker', icon: TrendingUp, description: 'Scrolling deal ticker' },
  my_tasks: { label: 'My Tasks', icon: ListTodo, description: 'Personal task list' },
};

const DEFAULT_MODULE_ORDER = [
  'animated_counters', 'latest_opportunities', 'activity_feed',
  'news_articles', 'announcements', 'commodity_tracker', 'opportunity_ticker', 'my_tasks'
];

const UserDashboard = () => {
  const { user } = useAuth();
  const [dashboardStats, setDashboardStats] = useState(null);
  const [tierStatus, setTierStatus] = useState(null);
  const [dashboardConfig, setDashboardConfig] = useState(null);
  const [tickerDeals, setTickerDeals] = useState([]);
  const [activities, setActivities] = useState([]);
  const [articles, setArticles] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [myDeals, setMyDeals] = useState([]);
  const [latestDeals, setLatestDeals] = useState([]);
  const [loading, setLoading] = useState(true);

  // User preferences state
  const [showSettings, setShowSettings] = useState(false);
  const [moduleOrder, setModuleOrder] = useState(DEFAULT_MODULE_ORDER);
  const [hiddenModules, setHiddenModules] = useState([]);
  const [savingPrefs, setSavingPrefs] = useState(false);

  usePageTitle('Dashboard');

  useEffect(() => {
    fetchAllData();
    fetchPreferences();
  }, []);

  const fetchPreferences = async () => {
    try {
      const res = await api.get('/dashboard/preferences');
      if (res.data.module_order?.length > 0) {
        // Merge saved order with new default items that weren't in saved order
        const savedIds = new Set(res.data.module_order);
        const newItems = DEFAULT_MODULE_ORDER.filter(id => !savedIds.has(id));
        const mergedOrder = [...res.data.module_order, ...newItems];
        setModuleOrder(mergedOrder);
      } else {
        setModuleOrder(DEFAULT_MODULE_ORDER);
      }
      setHiddenModules(res.data.hidden_modules || []);
    } catch (error) {
      // Use defaults silently
    }
  };

  const savePreferences = async () => {
    setSavingPrefs(true);
    try {
      await api.put('/dashboard/preferences', {
        module_order: moduleOrder,
        hidden_modules: hiddenModules,
      });
      toast.success('Dashboard layout saved');
      setShowSettings(false);
    } catch (error) {
      toast.error('Failed to save preferences');
    } finally {
      setSavingPrefs(false);
    }
  };

  const fetchAllData = async () => {
    try {
      const [
        statsRes, tierRes, configRes, tickerRes, activityRes, newsRes, announcementsRes, dealsRes,
      ] = await Promise.all([
        api.get('/dashboard/stats').catch(() => ({ data: {} })),
        api.get('/tiers/my-status').catch(() => ({ data: {} })),
        api.get('/dashboard/config').catch(() => ({ data: {} })),
        api.get('/dashboard/ticker').catch(() => ({ data: [] })),
        api.get('/dashboard/activity').catch(() => ({ data: [] })),
        api.get('/news/latest?limit=5').catch(() => ({ data: { articles: [] } })),
        api.get('/announcements').catch(() => ({ data: [] })),
        api.get('/deals').catch(() => ({ data: [] })),
      ]);

      setDashboardStats(statsRes.data);
      setTierStatus(tierRes.data);
      setDashboardConfig(configRes.data);
      setTickerDeals(tickerRes.data);
      setActivities(activityRes.data);
      setArticles(newsRes.data?.articles || []);
      setAnnouncements(Array.isArray(announcementsRes.data) ? announcementsRes.data.slice(0, 5) : []);

      const allDeals = Array.isArray(dealsRes.data) ? dealsRes.data : [];
      const userId = user?.id || user?._id;
      setMyDeals(allDeals.filter(d => d.user_id === userId).slice(0, 10));
      setLatestDeals(allDeals.slice(0, 10));
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  // Build effective config: merge admin config with user preferences
  const getEffectiveConfig = useCallback(() => {
    if (!dashboardConfig) return {};
    const effective = { ...dashboardConfig };
    for (const key of Object.keys(effective)) {
      if (effective[key]?.enabled !== false && hiddenModules.includes(key)) {
        effective[key] = { ...effective[key], enabled: false };
      }
    }
    return effective;
  }, [dashboardConfig, hiddenModules]);

  // Is a module available (admin enabled)?
  const isAdminEnabled = (key) => dashboardConfig?.[key]?.enabled !== false;

  const toggleModuleVisibility = (key) => {
    setHiddenModules(prev =>
      prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
    );
  };

  const onDragEnd = (result) => {
    if (!result.destination) return;
    const newOrder = Array.from(moduleOrder);
    const [removed] = newOrder.splice(result.source.index, 1);
    newOrder.splice(result.destination.index, 0, removed);
    setModuleOrder(newOrder);
  };

  // Determine account type for layout selection
  const accountType = (
    dashboardStats?.account_type ||
    tierStatus?.account_type ||
    ''
  ).toLowerCase().replace(/\//g, '_');

  // Map account types to dashboard layouts
  const getDashboardLayout = () => {
    const effectiveConfig = getEffectiveConfig();
    const commonProps = {
      stats: dashboardStats,
      tierStatus,
      config: effectiveConfig,
      tickerDeals,
      activities,
      articles,
      announcements,
      moduleOrder,
    };

    switch (accountType) {
      case 'seller':
        return <SellerDashboard {...commonProps} myDeals={myDeals} />;
      case 'buyer':
        return <BuyerDashboard {...commonProps} latestDeals={latestDeals} />;
      case 'buyer_seller':
      case 'broker':
        return <BrokerDashboard {...commonProps} myDeals={myDeals} latestDeals={latestDeals} />;
      default:
        return <BuyerDashboard {...commonProps} latestDeals={latestDeals} />;
    }
  };

  // Settings panel data
  const visibleModules = moduleOrder.filter(k => !hiddenModules.includes(k) && isAdminEnabled(k));
  const hiddenByUser = moduleOrder.filter(k => hiddenModules.includes(k) && isAdminEnabled(k));
  const disabledByAdmin = DEFAULT_MODULE_ORDER.filter(k => !isAdminEnabled(k));

  if (loading) {
    return (
      <Sidebar>
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="flex flex-col items-center gap-3">
            <div className="animate-spin rounded-full h-10 w-10 border-2 border-gray-300 border-t-blue-600" />
            <p className="text-sm text-gray-500">Loading dashboard...</p>
          </div>
        </div>
      </Sidebar>
    );
  }

  return (
    <Sidebar>
      <div className="p-4 sm:p-6 md:p-8 bg-gray-50 min-h-screen relative overflow-hidden">
        {/* Settings button */}
        <button
          onClick={() => setShowSettings(true)}
          className="absolute top-4 right-4 sm:top-6 sm:right-6 md:top-8 md:right-8 p-2 rounded-lg bg-white border border-gray-200 shadow-sm hover:shadow-md hover:bg-gray-50 transition z-10"
          title="Customize Dashboard"
        >
          <Settings2 className="w-5 h-5 text-gray-500" />
        </button>

        {getDashboardLayout()}

        {/* Settings Panel (slide-over) - inline JSX, not a sub-component */}
        {showSettings && (
          <>
            <div className="fixed inset-0 bg-black/30 z-40 transition-opacity" onClick={() => setShowSettings(false)} />
            <div className="fixed top-0 right-0 h-full w-full sm:w-96 bg-white shadow-2xl z-50 flex flex-col overflow-hidden">
              {/* Header */}
              <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
                <div>
                  <h2 className="text-lg font-bold text-gray-900">Customize Dashboard</h2>
                  <p className="text-xs text-gray-500 mt-0.5">Drag to reorder, toggle to show/hide</p>
                </div>
                <button onClick={() => setShowSettings(false)} className="p-2 rounded-lg hover:bg-gray-100 transition">
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto px-5 py-4 space-y-6">
                {/* Visible modules - draggable */}
                <div>
                  <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Visible Modules</h3>
                  <DragDropContext onDragEnd={onDragEnd}>
                    <Droppable droppableId="user-modules">
                      {(provided) => (
                        <div ref={provided.innerRef} {...provided.droppableProps} className="space-y-2">
                          {visibleModules.map((key, index) => {
                            const meta = MODULE_META[key];
                            if (!meta) return null;
                            const IconComp = meta.icon;
                            return (
                              <Draggable key={key} draggableId={key} index={index}>
                                {(provided, snapshot) => (
                                  <div
                                    ref={provided.innerRef}
                                    {...provided.draggableProps}
                                    className={`flex items-center gap-3 p-3 rounded-lg border transition ${
                                      snapshot.isDragging ? 'shadow-lg ring-2 ring-blue-400 bg-white' : 'bg-gray-50 border-gray-200'
                                    }`}
                                  >
                                    <div {...provided.dragHandleProps} className="cursor-grab">
                                      <GripVertical className="w-4 h-4 text-gray-400" />
                                    </div>
                                    <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#EFF6FF' }}>
                                      <IconComp className="w-4 h-4 text-blue-600" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <p className="text-sm font-medium text-gray-800">{meta.label}</p>
                                      <p className="text-xs text-gray-500 truncate">{meta.description}</p>
                                    </div>
                                    <button onClick={() => toggleModuleVisibility(key)} className="p-1.5 rounded-lg hover:bg-gray-200 transition flex-shrink-0" title="Hide module">
                                      <Eye className="w-4 h-4 text-gray-500" />
                                    </button>
                                  </div>
                                )}
                              </Draggable>
                            );
                          })}
                          {provided.placeholder}
                        </div>
                      )}
                    </Droppable>
                  </DragDropContext>
                  {visibleModules.length === 0 && (
                    <p className="text-sm text-gray-400 text-center py-4">All modules are hidden</p>
                  )}
                </div>

                {/* Hidden by user */}
                {hiddenByUser.length > 0 && (
                  <div>
                    <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Hidden Modules</h3>
                    <div className="space-y-2">
                      {hiddenByUser.map(key => {
                        const meta = MODULE_META[key];
                        if (!meta) return null;
                        const IconComp = meta.icon;
                        return (
                          <div key={key} className="flex items-center gap-3 p-3 rounded-lg border border-dashed border-gray-200 bg-white opacity-70">
                            <div className="w-4" />
                            <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#F3F4F6' }}>
                              <IconComp className="w-4 h-4 text-gray-400" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-500">{meta.label}</p>
                            </div>
                            <button onClick={() => toggleModuleVisibility(key)} className="p-1.5 rounded-lg hover:bg-gray-100 transition flex-shrink-0" title="Show module">
                              <EyeOff className="w-4 h-4 text-gray-400" />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Disabled by admin */}
                {disabledByAdmin.length > 0 && (
                  <div>
                    <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Disabled by Admin</h3>
                    <div className="space-y-2">
                      {disabledByAdmin.map(key => {
                        const meta = MODULE_META[key];
                        if (!meta) return null;
                        const IconComp = meta.icon;
                        return (
                          <div key={key} className="flex items-center gap-3 p-3 rounded-lg border border-gray-100 bg-gray-50 opacity-50">
                            <div className="w-4" />
                            <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 bg-gray-100">
                              <IconComp className="w-4 h-4 text-gray-300" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-400">{meta.label}</p>
                              <p className="text-xs text-gray-300">Disabled by administrator</p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="px-5 py-4 border-t border-gray-200 flex gap-3">
                <button
                  onClick={() => { setModuleOrder(DEFAULT_MODULE_ORDER); setHiddenModules([]); }}
                  className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition"
                >
                  Reset to Default
                </button>
                <button
                  onClick={savePreferences}
                  disabled={savingPrefs}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition disabled:opacity-50"
                >
                  <Save className="w-4 h-4" />
                  {savingPrefs ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </Sidebar>
  );
};

export default UserDashboard;
